import { Anchor, Box, Button, Collapse, Group, Loader, Paper, Stack, Switch, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerSafeTicket } from "@msc/shared";

import { isJiraReauthRequired, useTicketDetail } from "../../hooks/useCustomerView.js";
import { referencedAttachmentIds } from "../../lib/customerView/attachments.js";
import { priorityLabel } from "../../lib/customerView/priorityLabel.js";
import { absoluteDate, daysOpen, relativeTime } from "../../lib/customerView/relativeTime.js";
import { formatIsoDate } from "../../lib/date.js";
import { ChecklistPanel } from "./ChecklistPanel.js";
import { CommentComposer } from "./CommentComposer.js";
import { ReconnectBanner } from "./ReconnectBanner.js";
import { RichHtml } from "./RichHtml.js";
import { StatusBadge } from "./StatusBadge.js";
import { TicketMetaEditors } from "./TicketMetaEditors.js";
import { TransitionMenu } from "./TransitionMenu.js";

interface Props {
  ticket: CustomerSafeTicket;
  focusMode: boolean;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  objectKey: string | null;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Kopf aus der Listenzeile (sofort da), Rumpf aus dem Detail-Query:
 * Statuswechsel, Feld-Editoren, Beschreibung, Kommentare (interne nur per
 * Schalter, der je Ticket auf AUS zurückfällt), Checkliste, Dateien.
 * Antragstyp bleibt reine Anzeige (Scheibe 5). Das Kommentarformular steht
 * als klebende Fußzeile ganz unten — Begründung dort.
 */
export function TicketDetail({ ticket, focusMode, onPrev, onNext, hasPrev, hasNext, objectKey }: Props) {
  const { t } = useTranslation();
  const [descriptionOpen, setDescriptionOpen] = useState(true);
  const [showInternal, setShowInternal] = useState(false);
  useEffect(() => setShowInternal(false), [ticket.key]);

  const detail = useTicketDetail(ticket.key);
  const d = detail.data;
  const comments = (showInternal ? (d?.comments ?? []) : (d?.comments ?? []).filter((c) => c.isPublic))
    .slice()
    .sort((a, b) => b.createdIso.localeCompare(a.createdIso));
  const hasInternal = (d?.comments ?? []).some((c) => !c.isPublic);
  // Bereits inline referenzierte Bilder (Beschreibung/Kommentare) nicht noch
  // einmal als Download zeigen; alle anderen Anhänge — auch nicht
  // referenzierte Bilder, SVG, sonstige Dateien — bleiben sichtbar.
  const referencedIds = referencedAttachmentIds([d?.descriptionHtml ?? "", ...(d?.comments ?? []).map((c) => c.bodyHtml)]);
  const files = (d?.attachments ?? []).filter((a) => !referencedIds.has(a.id));
  const nav = (
    <Group gap="xs">
      <Button size="xs" variant="default" onClick={onPrev} disabled={!hasPrev}>{t("pages.customerView.cockpit.prev")}</Button>
      <Button size="xs" variant="default" onClick={onNext} disabled={!hasNext}>{t("pages.customerView.cockpit.next")}</Button>
    </Group>
  );

  return (
    <Stack gap="md">
      <Box>
        <Group gap="xs" wrap="wrap">
          <Text size="sm" c="dimmed" ff="monospace">{ticket.key}</Text>
          <Text size="sm" c="dimmed">{ticket.issueType}</Text>
          <StatusBadge status={ticket.status} category={ticket.statusCategory} />
          {focusMode && <Box ml="auto">{nav}</Box>}
        </Group>
        <Group justify="space-between" align="flex-start" mt={4}>
          <Title order={3}>{ticket.title}</Title>
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={() => window.print()}>{t("pages.customerView.cockpit.print")}</Button>
            {d && <Button size="xs" variant="default" component="a" href={d.browseUrl} target="_blank" rel="noopener">{t("pages.customerView.cockpit.openInJira")}</Button>}
            <Box data-print="hide">
              <TransitionMenu key={ticket.key} ticketKey={ticket.key} objectKey={objectKey} currentStatus={ticket.status} currentDueDate={d?.dueDate ?? null} />
            </Box>
          </Group>
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          {t("pages.customerView.cockpit.created")} <span title={absoluteDate(ticket.createdIso)}>{relativeTime(ticket.createdIso)}</span>
          {" · "}{t("pages.customerView.cockpit.updatedLabel")} <span title={absoluteDate(ticket.updatedIso)}>{relativeTime(ticket.updatedIso)}</span>
          {ticket.statusCategory !== "done" && (
            <>{" · "}{t("pages.customerView.cockpit.openFor", { days: daysOpen(ticket.createdIso) })}</>
          )}
        </Text>
      </Box>

      {detail.isLoading && <Loader size="sm" />}
      {detail.isError && (
        isJiraReauthRequired(detail.error) ? <ReconnectBanner /> : <Text size="sm" c="red">{t("pages.customerView.cockpit.detailError")}</Text>
      )}

      {d && (
        <Stack gap="xs">
          <Box data-print="hide">
            <TicketMetaEditors key={ticket.key} detail={d} objectKey={objectKey} />
          </Box>
          <Text size="sm"><b>{t("pages.customerView.cockpit.requestType")}:</b> {d.requestType?.name ?? "—"}</Text>
          {/* Reine Leseansicht für den Druck: die Editoren oben sind interaktiv
              und werden per data-print="hide" ausgeblendet, die Metadaten
              sollen im Ausdruck aber trotzdem sichtbar bleiben. */}
          <Stack gap={4} data-print="only">
            <Text size="sm"><b>{t("pages.customerView.cockpit.priority")}:</b> {priorityLabel(d.priority?.name)}</Text>
            <Text size="sm"><b>{t("pages.customerView.cockpit.dueDate")}:</b> {formatIsoDate(d.dueDate)}</Text>
            <Text size="sm"><b>{t("pages.customerView.cockpit.assignee")}:</b> {d.assignee?.displayName ?? t("pages.customerView.cockpit.unassigned")}</Text>
          </Stack>
        </Stack>
      )}

      {d && (
        <Box>
          <Anchor component="button" type="button" size="sm" onClick={() => setDescriptionOpen((o) => !o)}>
            {descriptionOpen ? t("pages.customerView.cockpit.hideDescription") : t("pages.customerView.cockpit.showDescription")}
          </Anchor>
          <Collapse in={descriptionOpen}>
            <Paper withBorder p="sm" mt="xs" bg="var(--mantine-color-gray-0)">
              {d.descriptionHtml.trim() ? <RichHtml html={d.descriptionHtml} /> : <Text size="sm">{t("pages.customerView.cockpit.noDescription")}</Text>}
            </Paper>
          </Collapse>
        </Box>
      )}

      {d && (
        <Box>
          <Group justify="space-between">
            <Text size="sm" fw={600}>{t("pages.customerView.cockpit.comments")}</Text>
            {hasInternal && (
              <Switch size="xs" color="yellow" label={t("pages.customerView.cockpit.showInternal")} checked={showInternal} onChange={(e) => setShowInternal(e.currentTarget.checked)} />
            )}
          </Group>
          {comments.length === 0 ? (
            <Text size="sm" c="dimmed" mt="xs">{t("pages.customerView.cockpit.noComments")}</Text>
          ) : (
            <Stack gap="sm" mt="xs">
              {comments.map((c) => (
                <Paper key={c.id} withBorder p="sm" style={c.isPublic ? undefined : { borderLeft: "4px solid var(--mantine-color-yellow-5)", background: "var(--mantine-color-yellow-0)" }}>
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Text size="xs" fw={600}>{c.authorName}</Text>
                      {!c.isPublic && <Text size="xs" c="yellow.9" fw={700} tt="uppercase">{t("pages.customerView.cockpit.internal")}</Text>}
                    </Group>
                    <Text size="xs" c="dimmed" title={absoluteDate(c.createdIso)}>{relativeTime(c.createdIso)}</Text>
                  </Group>
                  <Box mt={4}><RichHtml html={c.bodyHtml} /></Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {d && <Box data-print="hide"><ChecklistPanel key={ticket.key} ticketKey={ticket.key} objectKey={objectKey} /></Box>}

      {files.length > 0 && (
        <Box>
          <Text size="sm" fw={600}>{t("pages.customerView.cockpit.files")}</Text>
          <Stack gap={4} mt="xs">
            {files.map((a) => (
              <Anchor key={a.id} size="sm" href={`/api/v1/customer-view/attachments/${a.id}`} target="_blank" rel="noopener">
                {a.filename} ({formatBytes(a.sizeBytes)})
              </Anchor>
            ))}
          </Stack>
        </Box>
      )}

      {focusMode && <Group justify="space-between" pt="sm">{nav}</Group>}

      {/* Die Kommentarbox klebt am unteren Rand des Detailbereichs, statt ganz
          unten auf den Leser zu warten: Bei langen Verläufen scrollte man
          bisher an Kommentaren, Checkliste und Dateien vorbei, nur um zu
          antworten.

          Zwei Bedingungen, beide notwendig — nachgemessen, nicht vermutet:
          Sie muss ein DIREKTES Kind dieses `Stack` sein (`position: sticky`
          klebt nur innerhalb des eigenen Elternelements; im Kommentar-Kasten
          wäre sie mit dessen Ende weggescrollt), und sie muss ZULETZT im
          Fluss stehen. Denn `bottom: 0` verschiebt ein Element nur nach
          UNTEN, nie nach oben: Sobald seine natürliche Position über den
          unteren Rand des Scrollbereichs gewandert ist, klebt nichts mehr.
          Nach den Kommentaren platziert war sie ab der Checkliste wieder weg.

          Die Lesereihenfolge des Kunden bleibt davon unberührt — Kommentare
          stehen weiter über der Checkliste (Scheibe 3) —, und der Ausdruck
          blendet das Formular ohnehin aus (`data-print="hide"`). */}
      {d && (
        <Box
          data-print="hide"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            background: "var(--mantine-color-body)",
            borderTop: "1px solid var(--mantine-color-gray-3)",
            paddingTop: "var(--mantine-spacing-xs)",
          }}
        >
          <CommentComposer key={ticket.key} ticketKey={ticket.key} objectKey={objectKey} />
        </Box>
      )}
    </Stack>
  );
}
